import { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const links = [
  { to: "/", label: "Home" },
  { to: "/team", label: "Team" },
  { to: "/events", label: "Events" },
  { to: "/partners", label: "Partners" },
  { to: "/netwerk", label: "Netwerk" },
  { to: "/contact", label: "Contact" },
];

const Logo = ({ dark }) => (
  <Link to="/" data-testid="nav-logo" className="flex items-center group">
    <img
      src={dark ? "/elsa-logo-navy.png" : "/elsa-logo-white.png"}
      alt="ELSA Brussel"
      className="h-12 w-auto object-contain transition-all duration-300"
    />
  </Link>
);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const dark = scrolled;

  return (
    <>
      <motion.header
        initial={{ y: -80 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
          scrolled ? "backdrop-blur-xl bg-white/75 border-b border-black/5" : "bg-transparent"
        }`}
        data-testid="main-navbar"
      >
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 h-20 flex items-center justify-between">
          <Logo dark={dark} />
          <nav className="hidden md:flex items-center gap-9">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-sm tracking-wide transition-colors relative py-1 ${
                    dark ? "text-[#0B0B0C]" : "text-white"
                  } ${isActive ? "font-600" : "font-400 opacity-80 hover:opacity-100"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/lid-worden"
              data-testid="nav-join-btn"
              className="text-sm font-600 px-5 py-2.5 rounded-full text-white transition-transform hover:-translate-y-0.5"
              style={{ background: "var(--elsa-blue)" }}
            >
              Lid worden
            </Link>
          </nav>
          <button
            className="md:hidden"
            onClick={() => setOpen(true)}
            data-testid="nav-mobile-open"
          >
            <Menu className={dark ? "text-[#0B0B0C]" : "text-white"} />
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] md:hidden"
            style={{ background: "var(--elsa-blue)" }}
            data-testid="mobile-menu"
          >
            <div className="flex justify-between items-center h-20 px-6">
              <Logo dark={false} />
              <button onClick={() => setOpen(false)} data-testid="nav-mobile-close">
                <X className="text-white" />
              </button>
            </div>
            <nav className="flex flex-col gap-2 px-6 mt-8">
              {[...links, { to: "/lid-worden", label: "Lid worden" }].map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                >
                  <Link
                    to={l.to}
                    onClick={() => setOpen(false)}
                    className="font-serif text-4xl text-white py-3 block"
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
